import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import '../styles/Members.css';

const POSITION_MAP = {
  mam_non: 'Mầm Non',
  tap_su: 'Tập Sự',
  culi_cap_cao: 'Culi Cấp Cao',
  bo_lao: 'Bô Lão',
  founder: 'Founder',
};

const TITLE_MAP = {
  founder: 'Founder',
  chu_nhiem: 'Chủ Nhiệm',
  pho_chu_nhiem: 'Phó Chủ Nhiệm',
  truong_ban_dao_tao: 'Trưởng Ban Đào Tạo',
  pho_ban_dao_tao: 'Phó Ban Đào Tạo',
  truong_ban_su_kien: 'Trưởng Ban Sự Kiện',
  pho_ban_su_kien: 'Phó Ban Sự Kiện',
};

const TITLE_ORDER = {
  founder: 7,
  chu_nhiem: 6,
  pho_chu_nhiem: 5,
  truong_ban_dao_tao: 4,
  truong_ban_su_kien: 3,
  pho_ban_dao_tao: 2,
  pho_ban_su_kien: 1,
};

const BAN_LABEL = {
  ban_dieu_hanh: 'Ban Điều Hành',
  ban_dao_tao: 'Ban Đào Tạo',
  ban_su_kien: 'Ban Sự Kiện',
  ky_thuat: 'Kỹ Thuật',
};

const BAN_PRIORITY = ['ban_dieu_hanh', 'ban_dao_tao', 'ban_su_kien', 'ky_thuat'];
const VISIBLE_BANS = ['ban_dieu_hanh', 'ban_dao_tao', 'ban_su_kien'];

const CTF_FIELD_MAP = {
  reverse_engineering: 'Reverse Engineering',
  pwnable: 'Binary Exploitation',
  web_exploitation: 'Web Exploitation',
  cryptography: 'Cryptography',
  forensics: 'Digital Forensics',
  osint: 'Open Source Intelligence',
};

const pickPrimaryBan = (roleRaw) => {
  const roles = Array.isArray(roleRaw) ? roleRaw : (roleRaw ? [roleRaw] : []);
  for (const key of BAN_PRIORITY) {
    if (roles.includes(key)) return key;
  }
  return null;
};

const pickAllBans = (roleRaw) => {
  const roles = Array.isArray(roleRaw) ? roleRaw : (roleRaw ? [roleRaw] : []);
  return VISIBLE_BANS.filter(k => roles.includes(k));
};

const PhotoCarousel = ({ photos }) => {
  const [active, setActive] = useState(0);

  const next = useCallback(() => {
    setActive((prev) => (prev + 1) % photos.length);
  }, [photos.length]);

  useEffect(() => {
    if (photos.length <= 1) return;
    const timer = setInterval(next, 3500);
    return () => clearInterval(timer);
  }, [photos.length, next]);

  if (photos.length === 1) {
    return (
      <div className="member-photo-carousel">
        <img
          src={`/uploads/profiles/${photos[0]}`}
          alt=""
          className="member-photo active static"
        />
      </div>
    );
  }

  return (
    <div className="member-photo-carousel">
      {photos.map((file, i) => (
        <img
          key={`${file}-${i}`}
          src={`/uploads/profiles/${file}`}
          alt=""
          className={`member-photo ${i === active ? 'active' : ''}`}
        />
      ))}
    </div>
  );
};

const InitialsAvatar = ({ name, index }) => {
  const colors = [
    'linear-gradient(135deg, #0d9488, #00ff41)',
    'linear-gradient(135deg, #06b6d4, #0d9488)',
    'linear-gradient(135deg, #3b82f6, #06b6d4)',
    'linear-gradient(135deg, #8b5cf6, #3b82f6)',
    'linear-gradient(135deg, #ec4899, #8b5cf6)',
    'linear-gradient(135deg, #f59e0b, #ef4444)',
  ];
  const initials = (name || '?')
    .split(' ')
    .map((w) => w[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();

  return (
    <div
      className="member-initials-avatar"
      style={{ background: colors[index % colors.length] }}
    >
      <span>{initials}</span>
    </div>
  );
};

const MemberCard = ({ member, index, inView }) => {
  const displayPhotos = (() => {
    const photos = Array.isArray(member.photos) ? member.photos.filter(Boolean) : [];
    if (member.avatar) {
      const filtered = photos.filter((p) => p !== member.avatar);
      return [member.avatar, ...filtered];
    }
    return photos;
  })();

  const hasDisplayPhotos = displayPhotos.length > 0;

  const primaryBan = pickPrimaryBan(member.role);
  const allBans = pickAllBans(member.role);
  const showTitle = member.title && TITLE_MAP[member.title];
  const showBans = !showTitle && allBans.length > 0;
  const showPosition = !showTitle && !showBans && member.position;
  const isFounder = member.position === 'founder' || member.title === 'founder';

  return (
    <motion.div
      className="member-card"
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: 0.05 * index, duration: 0.45 }}
      whileHover={{ y: -6 }}
    >
      <Link to={`/isper/${member.username}`} className="member-card-link">
        <div className="member-photo-area">
          {hasDisplayPhotos ? (
            <PhotoCarousel photos={displayPhotos} />
          ) : (
            <InitialsAvatar name={member.fullName} index={index} />
          )}
        </div>
        <div className="member-info">
          <h3 className="member-name">{member.fullName}</h3>
          {showTitle && (
            <span className={`member-role-label member-role-${member.title}`}>{TITLE_MAP[member.title]}</span>
          )}
          {showBans && allBans.map(b => (
            <span key={b} className="member-ban-label">{BAN_LABEL[b]}</span>
          ))}
          {isFounder && (
            <span className="member-founder-badge" title="Người sáng lập">
              <span className="founder-star">★</span>
              <span>Founder</span>
            </span>
          )}
          {showPosition && (
            <span className="member-position">
              {POSITION_MAP[member.position] || member.position}
            </span>
          )}
          {member.ctfField && (
            <span className="member-ctf-field">
              {CTF_FIELD_MAP[member.ctfField] || member.ctfField}
            </span>
          )}
        </div>
      </Link>
    </motion.div>
  );
};

const GenSection = ({ group, inView, startIndex }) => {
  const [expanded, setExpanded] = useState(false);
  const { gen, leaders, others } = group;
  let idx = startIndex;

  return (
    <div className="gen-group">
      <motion.div
        className="gen-heading"
        initial={{ opacity: 0, x: -20 }}
        animate={inView ? { opacity: 1, x: 0 } : {}}
        transition={{ duration: 0.4 }}
      >
        <span className="gen-line" />
        <span className="gen-label">Gen {gen}</span>
        <span className="gen-line" />
      </motion.div>

      {leaders.length > 0 && (
        <div className="members-row members-row-leaders">
          {leaders.map((m) => {
            const i = idx++;
            return <MemberCard key={`${gen}-lead-${m.username}-${i}`} member={m} index={i} inView={inView} />;
          })}
        </div>
      )}

      {others.length > 0 && (
        <>
          <div className="gen-expand-row">
            <button
              type="button"
              className={`gen-expand-btn ${expanded ? 'is-open' : ''}`}
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
            >
              <span className="gen-expand-label">
                {expanded ? 'Less' : 'More'}
              </span>
              <svg
                className="gen-expand-chevron"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
          </div>

          <AnimatePresence initial={false}>
            {expanded && (
              <motion.div
                key={`gen-${gen}-others`}
                className="members-row members-row-members"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.35, ease: 'easeOut' }}
                style={{ overflow: 'hidden' }}
              >
                {others.map((m) => {
                  const i = idx++;
                  return <MemberCard key={`${gen}-oth-${m.username}-${i}`} member={m} index={i} inView={inView} />;
                })}
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}

      {leaders.length === 0 && others.length === 0 && (
        <p className="gen-empty-text">Không có thành viên nào trong Gen {gen}.</p>
      )}
    </div>
  );
};

const Members = ({ lang }) => {
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });
  const [groups, setGroups] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetch('/api/members')
      .then((res) => res.json())
      .then((data) => {
        const membersByGen = data.members || {};
        const collator = new Intl.Collator('vi', { sensitivity: 'base' });
        const grouped = Object.entries(membersByGen)
          .filter(([gen]) => gen !== 'unknown')
          .map(([gen, list]) => {
            const arr = list || [];
            const seen = new Set();
            const unique = arr.filter((m) => {
              const key = m.username || m.id;
              if (seen.has(key)) return false;
              seen.add(key);
              return true;
            });
            const leaders = unique
              .filter((m) => (m.title && TITLE_ORDER[m.title]) || pickPrimaryBan(m.role) === 'ban_dieu_hanh')
              .sort(
                (a, b) => (TITLE_ORDER[b.title] || 0) - (TITLE_ORDER[a.title] || 0)
              );
            const leaderKeys = new Set(leaders.map((m) => m.username || m.id));
            const others = unique
              .filter((m) => !leaderKeys.has(m.username || m.id))
              .sort((a, b) =>
                collator.compare(a.fullName || '', b.fullName || '')
              );
            return { gen, leaders, others };
          })
          .filter((g) => g.leaders.length > 0 || g.others.length > 0)
          .sort((a, b) => Number(a.gen) - Number(b.gen));
        setGroups(grouped);
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const fallbackMembers = [];
  if (loaded && groups.length === 0) {
    for (let i = 1; i <= 4; i++) {
      const name = lang[`MEMBER_${i}_NAME`];
      if (!name) break;
      fallbackMembers.push({
        fullName: name,
        position: '',
        ctfField: '',
        photos: [],
        username: `member-${i}`,
      });
    }
  }

  const hasFallback = loaded && groups.length === 0 && fallbackMembers.length > 0;

  let runningIndex = 0;

  return (
    <section className="members section" id="members" ref={ref}>
      <div className="container">
        <motion.div
          className="members-header"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <div className="terminal-section-header">
                <div className="terminal-section-titlebar">
                  root@ispclub:~/# cd /ispclub/members
                </div>
                <div className="terminal-section-body">
                  <span className="terminal-sh-line">&gt;_ sh members.sh</span>
                </div>
              </div>
          <h2 className="section-title">{lang.MEMBERS_TITLE || 'Our Team'}</h2>
          <p className="section-subtitle">
            {lang.MEMBERS_SUBTITLE || 'The talented and passionate people behind ISP Club.'}
          </p>
        </motion.div>

        {groups.map((group) => {
          const start = runningIndex;
          runningIndex += group.leaders.length + group.others.length;
          return (
            <GenSection
              key={`gen-${group.gen}`}
              group={group}
              inView={inView}
              startIndex={start}
            />
          );
        })}

        {hasFallback && (
          <div className="members-row">
            {fallbackMembers.map((member, idx) => (
              <MemberCard key={idx} member={member} index={idx} inView={inView} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Members;
